import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { BarChart3Icon, DatabaseIcon, RefreshCcwIcon } from "lucide-react";
import {
  type Key,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  AggFunc,
  ColumnDef,
  TimeBucket,
} from "@blis/insights/entities/types";
import {
  buildChartSeriesPlan,
  type ChartOverride,
  deriveChartKind,
  readKpiValue,
} from "@blis/insights/explore/chart-derive";
import {
  type ExploreFilterState,
  exploreFilterStateToFilters,
  serializeExploreFilterState,
} from "@blis/insights/explore/explore-filter-state";
import type { ExploreUrlState } from "@blis/insights/explore/explore-url-state";
import {
  buildStructuredQueryFromGridRequest,
  ExploreGridRequestError,
} from "@blis/insights/explore/grid-request";
import {
  buildStructuredQueryForSummary,
  reportResultColumnFields,
} from "@blis/insights/explore/report-query";
import { structuredConfigToGetRowsInput } from "@blis/insights/explore/ssrm-slice";
import type {
  GroupBy,
  Measure,
  StructuredQueryConfig,
} from "@blis/insights/explore/types";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import {
  AdminGrid,
  denseAdminGridTheme,
} from "@/features/admin/components/AdminGrid";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import { ExploreDateRangeSelector } from "../components/ExploreDateRangeSelector";
import { ExploreFilterBar } from "../components/ExploreFilterBar";
import { ExploreFilterMenu } from "../components/ExploreFilterMenu";
import { ExploreReportChart } from "../components/ExploreReportChart";
import { ExploreReportKpi } from "../components/ExploreReportKpi";
import {
  buildExploreColumnDefs,
  buildReportColumnDefs,
} from "../explore/buildExploreColumnDefs";
import type { ExploreUrlPatch } from "../explore/useExploreUrlState";

type ExploreEntity = RouterOutputs["explore"]["entities"][number];

type ViewMode = "rows" | "summary";
type ChartControlValue = Exclude<ChartOverride, "kpi" | null>;
type MeasureOption = {
  id: string;
  label: string;
  measure: Measure;
};

const DEFAULT_MEASURE_ID = "count:*";

const TIME_BUCKET_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "quarter", label: "Quarter" },
  { id: "year", label: "Year" },
] satisfies { id: TimeBucket; label: string }[];

const CHART_OPTIONS = [
  { id: "auto", label: "Auto" },
  { id: "bar", label: "Bar" },
  { id: "line", label: "Line" },
  { id: "donut", label: "Donut" },
] satisfies { id: ChartControlValue; label: string }[];

type ExploreEntityGridProps = {
  entity: ExploreEntity;
  urlState: ExploreUrlState;
  onUrlStateChange: (patch: ExploreUrlPatch) => void;
  onLoadError: (message: string | null) => void;
};

const SUMMARY_RELOAD_DEBOUNCE_MS = 300;

export function ExploreEntityGrid({
  entity,
  urlState,
  onUrlStateChange,
  onLoadError,
}: ExploreEntityGridProps) {
  const utils = trpc.useUtils();
  const viewMode = urlState.view;
  const exploreFilterState = urlState.filterState;
  const summaryGroupField = urlState.summary.groupField;
  const summaryBucket = urlState.summary.bucket;
  const summaryMeasureId = urlState.summary.measureId;
  const chartOverride = urlState.summary.chartOverride;
  const [summarySeriesField, setSummarySeriesField] = useState<string | null>(
    null,
  );
  const [exploreLoading, setExploreLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState<Record<string, unknown>[]>([]);
  const [summaryRowCount, setSummaryRowCount] = useState(0);
  const [queryConfig, setQueryConfig] = useState<StructuredQueryConfig | null>(
    null,
  );
  const summaryFetchGen = useRef(0);
  const lastLoadedFilterKeyRef = useRef<string | null>(null);

  const columnMeta = useMemo(
    (): ColumnDef[] =>
      entity.columns.map((c) => ({
        field: c.field,
        label: c.label,
        type: c.type,
        groupable: c.groupable,
        aggregatable: c.aggregatable,
        aggFuncs: c.aggFuncs,
        enumValues: c.enumValues,
        formatter: c.formatter,
        bucketable: c.bucketable,
        defaultVisible: c.defaultVisible,
        phi: c.phi,
      })),
    [entity.columns],
  );

  const columnMap = useMemo(
    () => new Map(columnMeta.map((column) => [column.field, column])),
    [columnMeta],
  );

  const resolvedFilters = useMemo(
    () =>
      exploreFilterStateToFilters(
        exploreFilterState,
        entity.defaultDateField,
        columnMap,
      ),
    [columnMap, entity.defaultDateField, exploreFilterState],
  );

  const filterStateKey = useMemo(
    () => serializeExploreFilterState(exploreFilterState),
    [exploreFilterState],
  );

  const groupableColumns = useMemo(
    () => columnMeta.filter((c) => !c.phi && c.groupable),
    [columnMeta],
  );

  const measureOptions = useMemo(
    () => buildMeasureOptions(columnMeta),
    [columnMeta],
  );

  const selectedGroupColumn =
    groupableColumns.find((column) => column.field === summaryGroupField) ??
    null;
  const selectedMeasureOption =
    measureOptions.find((option) => option.id === summaryMeasureId) ?? null;
  const selectedBucket = selectedGroupColumn?.bucketable
    ? summaryBucket
    : undefined;

  const seriesByColumns = useMemo(
    () =>
      groupableColumns.filter(
        (column) =>
          column.field !== summaryGroupField && !isTemporalColumn(column),
      ),
    [groupableColumns, summaryGroupField],
  );

  useEffect(() => {
    if (
      summarySeriesField != null &&
      !seriesByColumns.some((column) => column.field === summarySeriesField)
    ) {
      setSummarySeriesField(null);
    }
  }, [seriesByColumns, summarySeriesField]);

  const availableChartOptions = useMemo(
    () => chartOptionsForGroup(selectedGroupColumn, summaryRowCount),
    [selectedGroupColumn, summaryRowCount],
  );

  useEffect(() => {
    if (viewMode !== "summary") {
      return;
    }
    const chartStillValid = availableChartOptions.some(
      (option) => option.id === chartOverride,
    );
    if (!chartStillValid) {
      onUrlStateChange({ summary: { chartOverride: "auto" } });
    }
  }, [availableChartOptions, chartOverride, onUrlStateChange, viewMode]);

  const columnDefs = useMemo(
    () => buildExploreColumnDefs(columnMeta, entity.defaultDateField),
    [columnMeta, entity.defaultDateField],
  );

  const entityDef = useMemo(
    () => ({
      name: entity.name,
      columns: columnMeta,
    }),
    [columnMeta, entity.name],
  );

  const summaryQueryConfig = useMemo(() => {
    if (!selectedGroupColumn || !selectedMeasureOption) {
      return null;
    }

    const groupBy: GroupBy[] = [
      {
        field: selectedGroupColumn.field,
        ...(selectedBucket ? { bucket: selectedBucket } : undefined),
      },
    ];
    if (summarySeriesField) {
      groupBy.push({ field: summarySeriesField });
    }

    return buildStructuredQueryForSummary({
      entity: entityDef,
      groupBy,
      measures: [selectedMeasureOption.measure],
      filters: resolvedFilters,
    });
  }, [
    entityDef,
    resolvedFilters,
    selectedBucket,
    selectedGroupColumn,
    selectedMeasureOption,
    summarySeriesField,
  ]);

  const loadSummary = useCallback(
    async (config: StructuredQueryConfig) => {
      const generation = ++summaryFetchGen.current;
      const loadedFilterKey = filterStateKey;
      onLoadError(null);
      setSummaryLoading(true);

      try {
        const result = await utils.explore.getRows.fetch(
          structuredConfigToGetRowsInput(config),
        );
        if (generation !== summaryFetchGen.current) {
          return;
        }

        setQueryConfig(config);
        setSummaryRows(result.rows);
        setSummaryRowCount(result.totalRowCount);
        lastLoadedFilterKeyRef.current = loadedFilterKey;
      } catch (err: unknown) {
        if (generation !== summaryFetchGen.current) {
          return;
        }
        const message =
          err instanceof ExploreGridRequestError || err instanceof Error
            ? err.message
            : "Failed to load summary data";
        onLoadError(message);
        setQueryConfig(null);
        setSummaryRows([]);
        setSummaryRowCount(0);
      } finally {
        if (generation === summaryFetchGen.current) {
          setSummaryLoading(false);
        }
      }
    },
    [filterStateKey, onLoadError, utils],
  );

  const summaryFiltersStale =
    viewMode === "rows" &&
    lastLoadedFilterKeyRef.current != null &&
    filterStateKey !== lastLoadedFilterKeyRef.current;

  useEffect(() => {
    if (viewMode !== "summary" || !summaryQueryConfig) {
      return;
    }

    setSummaryLoading(true);
    ++summaryFetchGen.current;

    const timer = setTimeout(() => {
      void loadSummary(summaryQueryConfig);
    }, SUMMARY_RELOAD_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [loadSummary, summaryQueryConfig, viewMode]);

  const serverSide = useMemo<IServerSideDatasource>(() => {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        onLoadError(null);
        setExploreLoading(true);
        const req = params.request;
        try {
          const config = buildStructuredQueryFromGridRequest({
            entity: entityDef,
            rowGroupCols: [],
            groupKeys: [],
            valueCols: [],
            pivotCols: [],
            pivotMode: false,
            sortModel: req.sortModel,
            filters: resolvedFilters,
            startRow: req.startRow,
            endRow: req.endRow,
          });

          const result = await utils.explore.getRows.fetch(
            structuredConfigToGetRowsInput(config),
          );
          params.success({
            rowData: result.rows,
            rowCount: result.totalRowCount,
          });
        } catch (err: unknown) {
          const message =
            err instanceof ExploreGridRequestError || err instanceof Error
              ? err.message
              : "Failed to load explore data";
          onLoadError(message);
          params.fail();
        } finally {
          setExploreLoading(false);
        }
      },
    };
  }, [entityDef, onLoadError, resolvedFilters, utils]);

  const { gridProps } = useAdminServerSideGrid<Record<string, unknown>>({
    serverSideDatasource: serverSide,
    paginationPageSize: 100,
    refreshDeps: [entity.name, filterStateKey],
  });

  const exploreSideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressPivotMode: true,
            suppressPivots: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
        },
      ],
    }),
    [],
  );

  const switchMode = useCallback(
    (mode: ViewMode) => {
      onUrlStateChange({ view: mode });
      onLoadError(null);
    },
    [onLoadError, onUrlStateChange],
  );

  const setExploreFilterState = useCallback(
    (next: ExploreFilterState) => {
      onUrlStateChange({ filterState: next });
    },
    [onUrlStateChange],
  );

  const handleSummaryGroupChange = useCallback(
    (key: Key | null) => {
      if (key == null) return;
      const field = String(key);
      const nextColumn = groupableColumns.find((c) => c.field === field);
      setSummarySeriesField(null);
      onUrlStateChange({
        summary: {
          groupField: field,
          chartOverride: "auto",
          ...(nextColumn?.bucketable
            ? { bucket: defaultSummaryBucket(nextColumn) }
            : {}),
        },
      });
    },
    [groupableColumns, onUrlStateChange],
  );

  const handleSummaryRefresh = useCallback(() => {
    if (!summaryQueryConfig) {
      return;
    }

    void loadSummary(summaryQueryConfig);
  }, [loadSummary, summaryQueryConfig]);

  const summaryFields = useMemo(() => {
    if (queryConfig) {
      return reportResultColumnFields(queryConfig);
    }
    if (summaryRows.length > 0) {
      return Object.keys(summaryRows[0]);
    }
    return [];
  }, [queryConfig, summaryRows]);

  const summaryColumnLabels = useMemo(
    () => buildSummaryColumnLabels(queryConfig, columnMeta, measureOptions),
    [columnMeta, measureOptions, queryConfig],
  );

  const reportColumnDefs = useMemo(() => {
    if (summaryFields.length === 0) {
      return [];
    }
    return buildReportColumnDefs(
      summaryFields,
      columnMeta,
      summaryColumnLabels,
    );
  }, [columnMeta, summaryColumnLabels, summaryFields]);

  const chartKind = useMemo(() => {
    if (!queryConfig) return null;
    return deriveChartKind(
      queryConfig,
      columnMeta,
      summaryRows.length,
      chartOverride,
    );
  }, [chartOverride, columnMeta, queryConfig, summaryRows.length]);

  const chartPlan = useMemo(() => {
    if (!queryConfig) return null;
    return buildChartSeriesPlan(
      queryConfig,
      columnMeta,
      summaryRows.length,
      chartOverride,
    );
  }, [chartOverride, columnMeta, queryConfig, summaryRows.length]);

  const kpi = useMemo(() => {
    if (!queryConfig || chartKind !== "kpi") return null;
    const value = readKpiValue(summaryRows, queryConfig);
    if (!value) return null;
    return {
      ...value,
      label: selectedMeasureOption?.label ?? value.label,
    };
  }, [chartKind, queryConfig, selectedMeasureOption, summaryRows]);

  const summaryTitle =
    selectedGroupColumn && selectedMeasureOption
      ? `${selectedMeasureOption.label} by ${groupLabel(
          { field: selectedGroupColumn.field, bucket: selectedBucket },
          columnMeta,
        )}`
      : "Summary";

  const hasSummaryRows = summaryRows.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b bg-background">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2">
          <Tabs
            variant="segment"
            selectedKey={viewMode}
            onSelectionChange={(key) => {
              if (key === "rows" || key === "summary") {
                switchMode(key);
              }
            }}
          >
            <TabsList aria-label="Explore view">
              <Tab id="rows">
                <DatabaseIcon />
                Rows
              </Tab>
              <Tab id="summary" isDisabled={groupableColumns.length === 0}>
                <BarChart3Icon />
                Summary
                {summaryFiltersStale ? (
                  <span
                    className="size-2 rounded-full bg-primary"
                    aria-label="Filters changed since last summary"
                  />
                ) : null}
              </Tab>
            </TabsList>
          </Tabs>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <ExploreDateRangeSelector
              state={exploreFilterState}
              onChange={setExploreFilterState}
            />
            <ExploreFilterMenu
              state={exploreFilterState}
              columns={columnMeta}
              defaultDateField={entity.defaultDateField}
              onChange={setExploreFilterState}
            />
            {viewMode === "summary" ? (
              <Button
                variant="outline"
                size="sm"
                isDisabled={!summaryQueryConfig}
                isPending={summaryLoading}
                onPress={handleSummaryRefresh}
              >
                <RefreshCcwIcon />
                Refresh
              </Button>
            ) : null}
          </div>
        </div>

        {viewMode === "summary" ? (
          <div className="border-t bg-muted/20 px-4 py-3">
            <div
              className="flex flex-wrap items-end gap-x-6 gap-y-4"
              role="group"
              aria-label="Summary configuration"
            >
              <SummaryControl
                label="Group by"
                hint="Dimension for the chart and table"
              >
                <Select
                  aria-label="Group summary by"
                  selectedKey={summaryGroupField}
                  onSelectionChange={handleSummaryGroupChange}
                >
                  <SelectTrigger />
                  <SelectList items={groupableColumns}>
                    {(column) => (
                      <SelectItem id={column.field} textValue={column.label}>
                        {column.label}
                      </SelectItem>
                    )}
                  </SelectList>
                </Select>
              </SummaryControl>

              <SummaryControl
                label="Time bucket"
                hint={
                  selectedGroupColumn?.bucketable
                    ? "Roll up dates into periods"
                    : "Only available for date columns"
                }
              >
                {selectedGroupColumn?.bucketable ? (
                  <Select
                    aria-label="Time bucket"
                    selectedKey={selectedBucket ?? null}
                    onSelectionChange={(key) => {
                      if (key == null) return;
                      onUrlStateChange({
                        summary: { bucket: String(key) as TimeBucket },
                      });
                    }}
                  >
                    <SelectTrigger />
                    <SelectList items={TIME_BUCKET_OPTIONS}>
                      {(bucket) => (
                        <SelectItem id={bucket.id} textValue={bucket.label}>
                          {bucket.label}
                        </SelectItem>
                      )}
                    </SelectList>
                  </Select>
                ) : (
                  <Text size="2" muted className="min-h-9 px-1 py-2">
                    Not applicable
                  </Text>
                )}
              </SummaryControl>

              {selectedGroupColumn?.bucketable ? (
                <SummaryControl
                  label="Series by"
                  hint="Optional category for multiple lines"
                >
                  <Select
                    aria-label="Series by"
                    selectedKey={summarySeriesField ?? "none"}
                    onSelectionChange={(key) => {
                      if (key == null || key === "none") {
                        setSummarySeriesField(null);
                        return;
                      }
                      setSummarySeriesField(String(key));
                    }}
                  >
                    <SelectTrigger />
                    <SelectList
                      items={[
                        { id: "none", label: "None" },
                        ...seriesByColumns.map((column) => ({
                          id: column.field,
                          label: column.label,
                        })),
                      ]}
                    >
                      {(item) => (
                        <SelectItem id={item.id} textValue={item.label}>
                          {item.label}
                        </SelectItem>
                      )}
                    </SelectList>
                  </Select>
                </SummaryControl>
              ) : null}

              <SummaryControl
                label="Metric"
                hint="Value to aggregate per group"
              >
                <Select
                  aria-label="Summary metric"
                  selectedKey={summaryMeasureId}
                  onSelectionChange={(key) => {
                    if (key == null) return;
                    onUrlStateChange({ summary: { measureId: String(key) } });
                  }}
                >
                  <SelectTrigger />
                  <SelectList items={measureOptions}>
                    {(option) => (
                      <SelectItem id={option.id} textValue={option.label}>
                        {option.label}
                      </SelectItem>
                    )}
                  </SelectList>
                </Select>
              </SummaryControl>

              <SummaryControl
                label="Chart"
                hint="Override auto chart selection"
              >
                <Select
                  aria-label="Chart type"
                  selectedKey={chartOverride}
                  onSelectionChange={(key) => {
                    if (key == null) return;
                    onUrlStateChange({
                      summary: {
                        chartOverride: String(key) as ChartControlValue,
                      },
                    });
                  }}
                >
                  <SelectTrigger />
                  <SelectList items={availableChartOptions}>
                    {(option) => (
                      <SelectItem id={option.id} textValue={option.label}>
                        {option.label}
                      </SelectItem>
                    )}
                  </SelectList>
                </Select>
              </SummaryControl>
            </div>
          </div>
        ) : null}
      </div>

      <ExploreFilterBar
        state={exploreFilterState}
        columns={columnMeta}
        defaultDateField={entity.defaultDateField}
        onChange={setExploreFilterState}
      />

      {viewMode === "summary" ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b bg-muted/20 px-4 py-2">
            <Text size="2" className="truncate font-medium">
              {summaryTitle}
            </Text>
            <Text size="1" muted className="shrink-0 tabular-nums">
              {summaryLoading ? "Updating" : groupCountLabel(summaryRowCount)}
            </Text>
          </div>

          {!summaryLoading && !hasSummaryRows ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-10">
              <Text muted>No matching data</Text>
            </div>
          ) : (
            <>
              {kpi ? (
                <ExploreReportKpi label={kpi.label} value={kpi.value} />
              ) : null}
              {chartPlan && !kpi ? (
                <ExploreReportChart
                  rows={summaryRows}
                  plan={chartPlan}
                  labels={summaryColumnLabels}
                />
              ) : null}

              <div className="min-h-0 flex-1">
                <AdminGrid<Record<string, unknown>>
                  className="ag-theme-blis-full h-full w-full"
                  theme={denseAdminGridTheme}
                  rowModelType="clientSide"
                  rowData={summaryRows}
                  columnDefs={reportColumnDefs}
                  animateRows={false}
                  loading={summaryLoading}
                  pagination
                  paginationPageSize={100}
                  paginationPageSizeSelector={[50, 100, 250]}
                  autoSizeStrategy={{ type: "fitCellContents" }}
                  defaultColDef={{
                    minWidth: 120,
                    sortable: true,
                    resizable: true,
                    filterParams: { maxNumConditions: 1 },
                  }}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <AdminGrid<Record<string, unknown>>
          {...gridProps}
          className="ag-theme-blis-full h-full w-full flex-1"
          theme={denseAdminGridTheme}
          columnDefs={columnDefs}
          sideBar={exploreSideBar}
          animateRows={false}
          loading={exploreLoading}
          maxConcurrentDatasourceRequests={1}
          autoSizeStrategy={{ type: "fitCellContents" }}
          blockLoadDebounceMillis={250}
          serverSideSortAllLevels
          getRowId={exploreRowId}
          defaultColDef={{
            minWidth: 120,
            sortable: true,
            resizable: true,
          }}
        />
      )}
    </div>
  );
}

function SummaryControl({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-[10rem] max-w-xs flex-1 flex-col gap-1">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      {hint ? (
        <Text size="1" muted className="leading-snug">
          {hint}
        </Text>
      ) : null}
      {children}
    </div>
  );
}

function buildMeasureOptions(columns: readonly ColumnDef[]): MeasureOption[] {
  const options: MeasureOption[] = [
    {
      id: DEFAULT_MEASURE_ID,
      label: "Count results",
      measure: { field: "*", func: "count" },
    },
  ];

  for (const column of columns) {
    if (column.phi || !column.aggregatable || !column.aggFuncs) {
      continue;
    }

    for (const func of column.aggFuncs) {
      options.push({
        id: measureId(column.field, func),
        label: `${aggFuncLabel(func)} ${column.label}`,
        measure: { field: column.field, func },
      });
    }
  }

  return options;
}

function measureId(field: string, func: AggFunc): string {
  return `${func}:${field}`;
}

function aggFuncLabel(func: AggFunc): string {
  switch (func) {
    case "avg":
      return "Average";
    case "count":
      return "Count";
    case "count_distinct":
      return "Unique";
    case "max":
      return "Maximum";
    case "median":
      return "Median";
    case "min":
      return "Minimum";
    case "p95":
      return "95th percentile";
    case "sum":
      return "Total";
  }
}

function defaultSummaryBucket(column: ColumnDef): TimeBucket {
  return column.bucketable ? "day" : "month";
}

function chartOptionsForGroup(column: ColumnDef | null, groupCount: number) {
  if (!column) {
    return CHART_OPTIONS.filter((option) => option.id === "auto");
  }

  if (isTemporalColumn(column)) {
    return CHART_OPTIONS.filter((option) =>
      ["auto", "line", "bar"].includes(option.id),
    );
  }

  const categoricalOptions = ["auto", "bar"];
  if (groupCount > 0 && groupCount <= 8) {
    categoricalOptions.push("donut");
  }

  return CHART_OPTIONS.filter((option) =>
    categoricalOptions.includes(option.id),
  );
}

function isTemporalColumn(column: ColumnDef): boolean {
  return (
    column.bucketable === true ||
    column.type === "timestamp" ||
    column.type === "date"
  );
}

function buildSummaryColumnLabels(
  config: StructuredQueryConfig | null,
  columns: readonly ColumnDef[],
  measureOptions: readonly MeasureOption[],
) {
  const labels = new Map<string, string>();
  if (!config) {
    return labels;
  }

  const fields = reportResultColumnFields(config);
  for (const group of config.groupBy) {
    labels.set(group.field, groupLabel(group, columns));
  }

  config.measures.forEach((measure, index) => {
    const field = fields[config.groupBy.length + index];
    if (!field) return;
    labels.set(field, measureLabel(measure, measureOptions));
  });

  return labels;
}

function groupLabel(group: GroupBy, columns: readonly ColumnDef[]) {
  const label =
    columns.find((column) => column.field === group.field)?.label ??
    group.field;
  if (!group.bucket) {
    return label;
  }
  return `${bucketLabel(group.bucket)} of ${label}`;
}

function bucketLabel(bucket: TimeBucket): string {
  return (
    TIME_BUCKET_OPTIONS.find((option) => option.id === bucket)?.label ?? bucket
  );
}

function measureLabel(
  measure: Measure,
  measureOptions: readonly MeasureOption[],
) {
  return (
    measureOptions.find(
      (option) =>
        option.measure.field === measure.field &&
        option.measure.func === measure.func,
    )?.label ??
    measure.alias ??
    `${measure.func}_${measure.field}`
  );
}

function groupCountLabel(count: number) {
  return `${count.toLocaleString()} group${count === 1 ? "" : "s"}`;
}

function exploreRowId(params: { data?: Record<string, unknown> }) {
  const data = params.data;
  if (!data) return "";

  const resultId = data.test_order_result_id;
  if (resultId != null && resultId !== "") {
    return `tor:${resultId}`;
  }

  const parts = Object.keys(data)
    .sort()
    .map((key) => `${key}=${String(data[key])}`);
  return `row:${parts.join("|")}`;
}
